import React, { useEffect, useState } from "react";

/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Colors } from "../palettes/colorPalette";

export type ConnectionStepStatus = 'done' | 'ongoing' | 'error';

export interface ConnectionStep {
    label: string;
    status: ConnectionStepStatus;
}

// The steps of a multiplayer connection, in the order they happened. Everything before the
// last entry is done; only the last entry can be in progress or failed.
let steps: ConnectionStep[] = [];
const listeners: ((steps: ConnectionStep[]) => void)[] = [];

const publish = () => {
    for (const listener of [...listeners]) {
        listener(steps);
    }
};

/**
 * Reports the step the connection has reached. Whatever was in progress is checked off and
 * this becomes the in progress entry, so callers just announce each step as they get to it.
 */
export function setConnectionStep(label: string) {
    const last = steps[steps.length - 1];
    if (last && last.label === label) return;
    steps = [
        ...steps.map(step => step.status === 'error' ? step : { ...step, status: 'done' as const }),
        { label, status: 'ongoing' }
    ];
    publish();
}

/** Fails the step in progress, or adds a failed step if a label is given. */
export function setConnectionError(label?: string | null) {
    const last = steps[steps.length - 1];
    if (label && (!last || last.label !== label)) {
        steps = [...steps, { label, status: 'error' }];
    }
    else if (last) {
        steps = [...steps.slice(0, -1), { ...last, status: 'error' }];
    }
    else {
        return;
    }
    console.error("Connection error:", label);
    publish();
}

export function clearConnectionStatus() {
    if (steps.length === 0) return;
    steps = [];
    publish();
}

export function getConnectionSteps() {
    return steps;
}

/** Returns an unsubscribe function. */
export function subscribeToConnectionStatus(listener: (steps: ConnectionStep[]) => void) {
    listeners.push(listener);
    return () => {
        const index = listeners.indexOf(listener);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    };
}

const panelStyle = css`
    /* Positioned rather than laid out: as a flex child it re-balanced the menu and logo
       when it appeared. Sits above the logo, which spans the middle 25vh-75vh band. */
    position: absolute;
    top: 6vh;
    right: 2vw;
    box-sizing: border-box;
    width: 20vw;
    color: ${Colors.UIDescriptionText};
    background: ${Colors.UIBackground};
    border: inset 0.5vw ${Colors.UITextAndBorders};
    border-radius: 2vh;
    padding: 1vw;
`;

const listStyle = css`
    list-style: disc;
    margin: 0;
    padding-left: 1.2em;
    font-size: 1.2em;
`;

const itemStyle = css`
    margin-bottom: 0.6em;
    &:last-of-type {
        margin-bottom: 0;
    }
`;

const markerStyle = css`
    margin-right: 0.5em;
`;

const errorStyle = css`
    color: ${Colors.UIStat};
`;

/** True while the last reported step is a failure, i.e. the flow stopped with an error. */
export const useHasConnectionError = () => {
    const lastStepFailed = (current: ConnectionStep[]) =>
        current.length > 0 && current[current.length - 1].status === 'error';
    const [hasError, setHasError] = useState(() => lastStepFailed(getConnectionSteps()));

    useEffect(() => {
        setHasError(lastStepFailed(getConnectionSteps()));
        return subscribeToConnectionStatus(current => setHasError(lastStepFailed(current)));
    }, []);

    return hasError;
};

export const MultiplayerConnectionStatus = () => {
    const [currentSteps, setSteps] = useState<ConnectionStep[]>(getConnectionSteps);

    useEffect(() => {
        setSteps(getConnectionSteps()); // catch anything reported between render and subscribing
        return subscribeToConnectionStatus(setSteps);
    }, []);

    if (currentSteps.length === 0) return null;

    return (
        <div css={panelStyle}>
            <ul css={listStyle}>
                {currentSteps.map((step, index) => (
                    <li key={`${index}_${step.label}`} css={itemStyle}>
                        {step.status === 'done' &&
                            <FontAwesomeIcon css={markerStyle} icon={faCheck} />
                        }
                        {step.status === 'error' &&
                            <FontAwesomeIcon css={[markerStyle, errorStyle]} icon={faTimes} />
                        }
                        {step.label}{step.status === 'ongoing' ? '...' : ''}
                    </li>
                ))}
            </ul>
        </div>
    );
};
